/**
 * Serviço de Prompts do Sistema MEDICARE
 * Gerencia todas as operações relacionadas a prompts de IA
 */

import { ApiService, buildQueryString } from './api';
import {
  ApiResponse,
  PaginatedResponse,
  FiltroPrompts,
} from '../types';

// Interface específica para a resposta da API do backend
interface BackendApiResponse<T> {
  sucesso: boolean;
  mensagem: string;
  dados: T;
}

interface BackendPaginatedResponse<T> {
  prompts: T[];
  paginacao: {
    pagina_atual: number;
    total_paginas: number;
    total_registros: number;
    registros_por_pagina: number;
  };
}

// Interface para prompt
export interface Prompt {
  id: string;
  nome: string;
  descricao?: string;
  categoria: string;
  conteudo: string;
  variaveis: string[];
  ativo: boolean;
  temperatura: number;
  maxTokens: number;
  criado_por: number;
  data_criacao: string;
  data_atualizacao: string;
  uso_count?: number;
}

// Interface para criar/atualizar prompt
export interface PromptData {
  nome: string;
  descricao?: string;
  categoria: string;
  conteudo: string;
  variaveis: string[];
  ativo?: boolean;
  temperatura?: number;
  maxTokens?: number;
}

// Interface para dados do formulário
export interface PromptFormData {
  nome: string;
  descricao?: string;
  categoria: string;
  conteudo: string;
  variaveis: string;
  ativo: boolean;
  temperatura: number;
  maxTokens: number;
}

export class PromptsService {
  private static readonly BASE_URL = '/prompts';

  /**
   * Listar prompts com filtros e paginação
   */
  static async listar(filtros: FiltroPrompts = {}): Promise<BackendApiResponse<BackendPaginatedResponse<Prompt>>> {
    const queryString = buildQueryString(filtros);
    const url = queryString ? `${this.BASE_URL}?${queryString}` : this.BASE_URL;
    const response = await ApiService.get<any>(url);
    return response.data as BackendApiResponse<BackendPaginatedResponse<Prompt>>;
  }

  /**
   * Obter prompt por ID
   */
  static async obterPorId(id: string): Promise<ApiResponse<Prompt>> {
    return ApiService.get<Prompt>(`${this.BASE_URL}/${id}`);
  }

  /**
   * Criar novo prompt
   */
  static async criar(dados: PromptData): Promise<ApiResponse<Prompt>> {
    return ApiService.post<Prompt>(this.BASE_URL, dados);
  }

  /**
   * Atualizar prompt existente
   */
  static async atualizar(id: string, dados: Partial<PromptData>): Promise<ApiResponse<Prompt>> {
    return ApiService.put<Prompt>(`${this.BASE_URL}/${id}`, dados);
  }

  /**
   * Deletar prompt
   */
  static async deletar(id: string): Promise<ApiResponse<void>> {
    return ApiService.delete<void>(`${this.BASE_URL}/${id}`);
  }

  /**
   * Obter prompts por categoria
   */
  static async obterPorCategoria(categoria: string): Promise<ApiResponse<Prompt[]>> {
    return ApiService.get<Prompt[]>(`${this.BASE_URL}/categoria/${categoria}`);
  }

  /**
   * Obter todas as categorias
   */
  static async obterCategorias(): Promise<ApiResponse<string[]>> {
    return ApiService.get<string[]>(`${this.BASE_URL}/categorias`);
  }

  /**
   * Duplicar prompt
   */
  static async duplicar(id: string): Promise<ApiResponse<Prompt>> {
    return ApiService.post<Prompt>(`${this.BASE_URL}/${id}/duplicar`);
  }

  /**
   * Testar prompt
   */
  static async testar(id: string, variaveis: Record<string, string>): Promise<ApiResponse<{ resultado: string }>> {
    return ApiService.post<{ resultado: string }>(`${this.BASE_URL}/${id}/testar`, { variaveis });
  }

  /**
   * Obter estatísticas de uso
   */
  static async obterEstatisticas(): Promise<ApiResponse<{
    total: number;
    ativos: number;
    por_categoria: Record<string, number>;
    mais_usados: Array<{ id: string; nome: string; uso_count: number }>;
  }>> {
    return ApiService.get(`${this.BASE_URL}/estatisticas`);
  }

  /**
   * Exportar prompts
   */
  static async exportar(ids?: string[]): Promise<void> {
    const params = ids ? { ids: ids.join(',') } : {};
    const queryString = buildQueryString(params);
    const url = queryString ? `${this.BASE_URL}/exportar?${queryString}` : `${this.BASE_URL}/exportar`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('medicare_token')}`,
      },
    });
    
    if (response.ok) {
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `prompts_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    }
  }

  /**
   * Importar prompts
   */
  static async importar(arquivo: File): Promise<ApiResponse<{
    importados: number;
    erros: Array<{ linha: number; erro: string }>;
  }>> {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    
    return ApiService.post(`${this.BASE_URL}/importar`, formData);
  }
}

export default PromptsService;